/**
 * The ink wave's geometry for one button size and reading direction
 * (DESIGN.md "The ink wave"). Pure: no DOM, no styles.
 *
 * The front is a line slanted 35 degrees. It enters at the bottom corner on
 * the reading-start side and leaves by the opposite top corner. Its edge is
 * two liquid waves (wavelengths 6s and 3.9s, heights 0.7s and 0.5s), drawn
 * once as one SVG mask tile: the ink is their union. A tile placed at
 * (origin + u * N + e * T) puts the front u pixels along its normal and
 * slides its crests e pixels along the front.
 */

const ANGLE = (35 * Math.PI) / 180

export interface WaveGeometry {
  width: number
  height: number
  /** Along the front, toward the reading end. */
  T: [number, number]
  /** The front's normal, toward the side the ink has not reached. */
  N: [number, number]
  lambdaA: number
  lambdaB: number
  /** The front's distance when the button is empty, full, and past full (the sheen). */
  empty: number
  full: number
  beyond: number
  originX: number
  originY: number
  tileWidth: number
  tileHeight: number
  /** The wave tile and the sheen tile, as CSS `url()` images. */
  wave: string
  sheen: string
}

const cache = new Map<string, WaveGeometry>()
const round1 = (n: number) => Math.round(n * 10) / 10
const round2 = (n: number) => Math.round(n * 100) / 100

export function waveGeometry(
  width: number,
  height: number,
  ltr: boolean,
  size: number,
): WaveGeometry {
  const key = `${width}x${height}x${ltr ? 1 : 0}x${size}`
  const known = cache.get(key)
  if (known) return known

  const sin = Math.sin(ANGLE)
  const cos = Math.cos(ANGLE)
  const d = ltr ? 1 : -1
  const T: [number, number] = [d * cos, sin]
  const N: [number, number] = [d * sin, -cos]
  const lambdaA = 6 * size
  const lambdaB = 3.9 * size
  const ampA = 0.7 * size
  const ampB = 0.5 * size
  const glow = 6 * size
  const across = width * sin + height * cos
  const empty = -ampA - 2
  const full = across + ampA + 2
  const beyond = across + glow + ampA + 2
  const slide = 3 * lambdaA

  let dxMin = Number.POSITIVE_INFINITY
  let dxMax = Number.NEGATIVE_INFINITY
  let dyMin = Number.POSITIVE_INFINITY
  let dyMax = Number.NEGATIVE_INFINITY
  for (const u of [empty, beyond]) {
    for (const e of [-slide, slide]) {
      const x = u * N[0] + e * T[0]
      const y = u * N[1] + e * T[1]
      dxMin = Math.min(dxMin, x)
      dxMax = Math.max(dxMax, x)
      dyMin = Math.min(dyMin, y)
      dyMax = Math.max(dyMax, y)
    }
  }
  const originX = -dxMax
  const originY = -dyMax
  const tileWidth = Math.ceil(width + dxMax - dxMin)
  const tileHeight = Math.ceil(height + dyMax - dyMin)
  const corner: [number, number] = [(ltr ? 0 : width) - originX, height - originY]

  let vMin = Number.POSITIVE_INFINITY
  let vMax = Number.NEGATIVE_INFINITY
  for (const [qx, qy] of [
    [0, 0],
    [tileWidth, 0],
    [0, tileHeight],
    [tileWidth, tileHeight],
  ] as const) {
    const v = (qx - corner[0]) * T[0] + (qy - corner[1]) * T[1]
    vMin = Math.min(vMin, v)
    vMax = Math.max(vMax, v)
  }
  vMin -= 8
  vMax += 8
  const far = 2 * Math.hypot(tileWidth, tileHeight)
  const point = (v: number, w: number) =>
    `${round1(corner[0] + v * T[0] + w * N[0])} ${round1(corner[1] + v * T[1] + w * N[1])}`

  // The inked side of one wavy front: the wave itself, closed far behind it.
  const surface = (lambda: number, amp: number, phase: number) => {
    let path = ''
    for (let v = vMin; v <= vMax; v += 2) {
      path += `${path ? 'L' : 'M'}${point(v, amp * Math.sin((2 * Math.PI * v) / lambda + phase))}`
    }
    return `${path}L${point(vMax, -far)}L${point(vMin, -far)}Z`
  }
  // A mask reads only the shape, so no colour is written.
  const image = (inner: string) =>
    `url("data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${tileWidth}" height="${tileHeight}" viewBox="0 0 ${tileWidth} ${tileHeight}">${inner}</svg>`,
    )}")`
  const along = (k: number) => [round1(corner[0] + N[0] * k), round1(corner[1] + N[1] * k)]
  const [x1, y1] = along(ampA)
  const [x2, y2] = along(ampA - glow)

  const geometry: WaveGeometry = {
    width,
    height,
    T,
    N,
    lambdaA,
    lambdaB,
    empty,
    full,
    beyond,
    originX,
    originY,
    tileWidth,
    tileHeight,
    wave: image(
      `<path d="${surface(lambdaA, ampA, 0)}"/><path d="${surface(lambdaB, ampB, 1.3)}"/>`,
    ),
    sheen: image(
      `<defs><linearGradient id="g" gradientUnits="userSpaceOnUse" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"><stop offset="0" stop-opacity="0.95"/><stop offset="1" stop-opacity="0"/></linearGradient></defs><path fill="url(#g)" d="${surface(lambdaA, ampA, 0)}"/>`,
    ),
  }
  cache.set(key, geometry)
  return geometry
}

/** Where a tile sits: the front `u` along its normal, its crests slid `e` along it. */
export function waveOffset(g: WaveGeometry, u: number, e: number): [number, number] {
  return [round2(g.originX + u * g.N[0] + e * g.T[0]), round2(g.originY + u * g.N[1] + e * g.T[1])]
}

/** A tile's offset as a transform, and its inverse for the label copy that must stay still. */
export function waveTranslate(g: WaveGeometry, u: number, e: number, sign = 1): string {
  const [x, y] = waveOffset(g, u, e)
  return `translate(${sign * x}px, ${sign * y}px)`
}

/** Read the front and the slide back out of a computed transform (mid-animation included). */
export function readFront(g: WaveGeometry, transform: string) {
  const n = (transform.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/g) ?? []).map(Number.parseFloat)
  // A computed `matrix(a, b, c, d, x, y)`, or a `translate(x, y)` as written.
  const at = n.length >= 6 ? 4 : 0
  if (n.length < 2) return { u: g.empty, e: 0 }
  const x = (n[at] as number) - g.originX
  const y = (n[at + 1] as number) - g.originY
  return { u: x * g.N[0] + y * g.N[1], e: x * g.T[0] + y * g.T[1] }
}

/** Keep a slide inside one wavelength, so the resting position never drifts. */
export function wrapSlide(e: number, lambda: number): number {
  return ((e % lambda) + lambda) % lambda
}
